import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckinForm } from '../components/CheckinForm';
import { DailyGoodThing } from '../components/DailyGoodThing';
import { GraduationForm } from '../components/GraduationForm';
import { SocialActionPrompt } from '../components/SocialActionPrompt';
import {
  fetchConversations,
  fetchMatchPreference,
  requestRematch,
  saveMatchPreference,
  type Conversation,
  type MatchPreference,
  type MeResponse,
} from '../lib/api';

const ACTIVE_COMMUNITY_STORAGE_KEY = 'la-active-community';

/** Human-readable label + color tone for a conversation.status enum value. */
function describeStatus(status: string): {
  label: string;
  tone: 'active' | 'paused' | 'ended' | 'graduated';
} {
  switch (status) {
    case 'active':
      return { label: 'Active', tone: 'active' };
    case 'paused':
      return { label: 'Under review', tone: 'paused' };
    case 'graduated':
      return { label: 'Graduated', tone: 'graduated' };
    case 'ended':
    case 'dead':
    default:
      return { label: 'Ended', tone: 'ended' };
  }
}

/**
 * A short, content-free status line under each match name.
 *
 * Deliberately carries NO message timing. `conversation.lastMessageAt` is
 * bumped on every send — including peer messages still held by moderation
 * (pending / sync_flag) that are invisible to this user — so surfacing it
 * here ("last reply 2m ago") would leak that the peer wrote, and when,
 * before the message clears. That's exactly the fail-closed signal the chat
 * view goes out of its way to suppress (it derives its "last replied" line
 * from the visible stream only). The status alone is safe to show.
 */
function matchSubline(tone: string): string {
  switch (tone) {
    case 'active':
      return 'Open to keep the conversation going';
    case 'paused':
      return 'Paused for a quick review';
    case 'graduated':
      return 'Graduated — a good ending';
    default:
      return 'This conversation has ended';
  }
}

/** The member's landing view: their community identity, matches, and rituals.
 *  Desktop renders as a two-column dashboard (matches + "you" rituals); mobile
 *  collapses to a single stacked column. The app shell (left rail on desktop,
 *  bottom tab bar on native) lives in AppLayout. */
export function Home({ me }: { me: MeResponse }) {
  // Active membership — the one whose matches + rituals are shown. Persisted
  // so a multi-community founder doesn't lose context on refresh.
  const [activeCommunityId, setActiveCommunityId] = useState<string>(() => {
    try {
      const stored = window.localStorage.getItem(ACTIVE_COMMUNITY_STORAGE_KEY);
      if (stored && me.memberships.some((m) => m.communityId === stored)) {
        return stored;
      }
    } catch {
      // localStorage unavailable — fall through to default.
    }
    return me.memberships[0]?.communityId ?? '';
  });
  const membership =
    me.memberships.find((m) => m.communityId === activeCommunityId) ?? me.memberships[0];

  const [conversations, setConversations] = useState<Conversation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkin, setCheckin] = useState<'idle' | 'open' | 'done'>('idle');
  const [graduation, setGraduation] = useState<'idle' | 'open' | 'done'>('idle');
  const [matchPref, setMatchPref] = useState<MatchPreference | null>(null);
  const [matchPrefSaving, setMatchPrefSaving] = useState(false);
  const [rematching, setRematching] = useState(false);
  const [rematchNote, setRematchNote] = useState<string | null>(null);

  function selectCommunity(next: string) {
    setActiveCommunityId(next);
    setConversations(null);
    setMatchPref(null);
    setCheckin('idle');
    // Reset every per-membership flow too — otherwise an in-progress
    // graduation form (which submits against membership.communityId) or a
    // stale rematch note would carry over to the newly selected community
    // and act on the wrong one.
    setGraduation('idle');
    setRematchNote(null);
    try {
      window.localStorage.setItem(ACTIVE_COMMUNITY_STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }

  // Day-7 social-action follow-up emails land here with
  // ?social-action=<id>&outcome=<done|not_yet|changed_mind>. Pluck the
  // outcome out of the URL once and pass it down to SocialActionPrompt,
  // which marks the action and cleans the URL with replaceState.
  const deepLinkOutcome = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('social-action')) return undefined;
    const o = params.get('outcome');
    if (o === 'done' || o === 'not_yet' || o === 'changed_mind') {
      const url = new URL(window.location.href);
      url.searchParams.delete('social-action');
      url.searchParams.delete('outcome');
      window.history.replaceState({}, '', url.toString());
      return o;
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!membership) return;
    fetchConversations(membership.communityId)
      .then(setConversations)
      .catch(() => setError('Could not load your conversations.'));
    fetchMatchPreference(membership.communityId)
      .then(setMatchPref)
      .catch(() => setMatchPref('any'));
  }, [membership]);

  async function toggleMatchPref(next: MatchPreference) {
    // Re-tapping the already-selected chip is a no-op (the chips stay
    // enabled so the chosen one doesn't render in the disabled fade).
    if (!membership || matchPrefSaving || matchPref === next) return;
    setMatchPrefSaving(true);
    setMatchPref(next);
    const result = await saveMatchPreference(membership.communityId, next);
    setMatchPrefSaving(false);
    if (!result.ok) {
      setMatchPref(next === 'any' ? 'similar_tags' : 'any');
    }
  }

  // "Pair me with someone new" — actively asks the server for a fresh pairing
  // instead of waiting for the next scheduled matching pass.
  async function handleRematch() {
    if (!membership || rematching) return;
    setRematching(true);
    setRematchNote(null);
    try {
      const paired = await requestRematch(membership.communityId);
      const fresh = await fetchConversations(membership.communityId);
      setConversations(fresh);
      if (paired > 0) {
        setRematchNote(
          paired === 1 ? 'You have a new match — say hello.' : `You have ${paired} new matches.`,
        );
      } else {
        setRematchNote("You're in the pool — we'll pair you the moment someone's free.");
      }
    } catch {
      setRematchNote('Could not start a new pairing. Try again in a moment.');
    } finally {
      setRematching(false);
    }
  }

  if (!membership) return null;

  // After graduating, the home becomes a quiet "thank you" page.
  if (graduation === 'done') {
    return (
      <main>
        <div className="card">
          <span className="step-label">{membership.communityName}</span>
          <h1>Take care, {membership.nickname}.</h1>
          <p>
            Thank you for being part of LessAlone — your honest check-in is recorded and your
            matches have been told a fresh pairing is coming.
          </p>
          <p className="muted">
            If you ever want to come back, the same university email signs you straight back in. And
            if you are in crisis, you can always call or text <strong>988</strong>.
          </p>
        </div>
      </main>
    );
  }

  const activeCount = (conversations ?? []).filter(
    (c) => c.status === 'active' || c.status === 'paused',
  ).length;
  const subText =
    conversations == null
      ? ' '
      : activeCount > 0
        ? `${activeCount} ${activeCount === 1 ? 'person is' : 'people are'} in your corner right now.`
        : 'Your matches will appear here as soon as you’re paired.';
  const hasActive = (conversations ?? []).some(
    (c) => c.status === 'active' || c.status === 'paused',
  );

  return (
    <main className="home">
      <header className="home-head">
        <span className="step-label">{membership.communityName}</span>
        <h1>Hello, {membership.nickname}.</h1>
        <p className="home-sub">{subText}</p>

        {me.memberships.length > 1 && (
          <nav className="community-switcher" aria-label="Switch community">
            {me.memberships.map((m) => {
              const on = m.communityId === membership.communityId;
              return (
                <button
                  key={m.communityId}
                  type="button"
                  aria-current={on ? 'true' : undefined}
                  className={`community-chip${on ? ' on' : ''}`}
                  onClick={() => selectCommunity(m.communityId)}
                >
                  {m.communityName}
                </button>
              );
            })}
          </nav>
        )}
      </header>

      <div className="home-grid">
        {/* ---- Matches column ---- */}
        <section className="home-matches" aria-label="Your matches">
          <div className="col-head">
            <h2>Your matches</h2>
          </div>

          {error && <p className="error">{error}</p>}
          {!conversations && !error && <p className="muted">Loading…</p>}

          {conversations && conversations.length === 0 && (
            <div className="match-empty">
              <p className="match-empty-eyebrow">No match yet</p>
              <h3 className="match-empty-head">You're early — and that's okay.</h3>
              <p className="match-empty-body">
                We'll pair you with another member of {membership.communityName} as soon as someone
                is available, and we'll <strong>email you the moment we do</strong>. Most pairings
                happen within a day or two of joining.
              </p>
              <p className="match-empty-meta">
                Want to turn the email off, or hear about it in another way?{' '}
                <Link to="/settings/notifications">Notification settings</Link>
              </p>
            </div>
          )}

          {conversations && conversations.length > 0 && (
            <div className="match-list">
              {conversations.map((conv) => {
                const status = describeStatus(conv.status);
                const trimmedNick = conv.peerNickname.trim();
                const initial = trimmedNick.charAt(0).toUpperCase() || '?';
                const clay = (trimmedNick.charCodeAt(0) || 0) % 2 === 1;
                return (
                  <Link
                    key={conv.conversationId}
                    className={`match status-${status.tone}`}
                    to={`/conversations/${conv.conversationId}?community=${membership.communityId}`}
                  >
                    <span className={`match-av${clay ? ' clay' : ''}`} aria-hidden="true">
                      {initial}
                    </span>
                    <span className="match-body">
                      <span className="match-head">
                        <span className="match-peer">{conv.peerNickname}</span>
                        <span className={`status-pill status-${status.tone}-pill`}>
                          {status.label}
                        </span>
                      </span>
                      <span className="match-last">{matchSubline(status.tone)}</span>
                      {conv.peerTags.length > 0 && (
                        <span className="match-tags">
                          {conv.peerTags.map((tag) => {
                            const shared = conv.sharedTags.includes(tag);
                            return (
                              <span
                                key={tag}
                                className={`match-tag${shared ? ' shared' : ''}`}
                                title={shared ? 'You both like this' : undefined}
                              >
                                {tag.replace(/-/g, ' ')}
                              </span>
                            );
                          })}
                        </span>
                      )}
                    </span>
                    <span className="match-go" aria-hidden="true">
                      →
                    </span>
                  </Link>
                );
              })}
            </div>
          )}

          {conversations && !hasActive && (
            <div className="match-rematch">
              <button
                type="button"
                className="btn"
                disabled={rematching}
                onClick={() => void handleRematch()}
              >
                {rematching ? 'Finding someone…' : 'Pair me with someone new'}
              </button>
              {rematchNote && <p className="match-empty-meta">{rematchNote}</p>}
            </div>
          )}

          {/* A single match looks lonely beside the tall rituals stack — a
              dashed companion gives the column weight and reassures. */}
          {conversations && hasActive && activeCount <= 1 && (
            <div className="wait-card">
              <span className="eyebrow">While you wait</span>
              <h3>Want a second pairing?</h3>
              <p>
                You can hold space for one more conversation this week — no pressure, leave it if
                one feels like plenty.
              </p>
              <div className="wait-tip">
                <span className="ic" aria-hidden="true">
                  ✦
                </span>
                <p>A slow reply is normal here, not rejection. People write back when they can.</p>
              </div>
              <button
                type="button"
                className="wait-action"
                disabled={rematching}
                onClick={() => void handleRematch()}
              >
                {rematching ? 'Finding someone…' : 'Find a second match'}
              </button>
              {rematchNote && <p className="match-empty-meta">{rematchNote}</p>}
            </div>
          )}
        </section>

        {/* ---- "You" rituals column ---- */}
        <aside className="home-rituals" aria-label="Your reflections">
          <div className="home-card is-hero">
            <DailyGoodThing />
          </div>

          <div className="home-card">
            <SocialActionPrompt deepLinkOutcome={deepLinkOutcome} />
          </div>

          {/* Check-in: a light sage nudge when idle (clearly secondary to the
              daily reflection), a full card once you're answering it. */}
          {checkin === 'idle' ? (
            <div className="nudge">
              <div className="body">
                <h3>Your weekly check-in</h3>
                <p className="muted">
                  Three quiet questions — about a minute. It's how the pilot knows this is helping.
                </p>
              </div>
              <button type="button" className="nudge-go" onClick={() => setCheckin('open')}>
                Start
              </button>
            </div>
          ) : (
            <section className="home-card">
              <h2>Loneliness check-in</h2>
              {checkin === 'done' && (
                <p className="muted">
                  Thanks — your check-in is recorded. Taking it now and then is how we see whether
                  LessAlone is helping.
                </p>
              )}
              {checkin === 'open' && (
                <CheckinForm checkpoint="ongoing" onDone={() => setCheckin('done')} />
              )}
            </section>
          )}

          <div className="rituals-label">
            <span className="eyebrow">Preferences</span>
            <span className="ln" aria-hidden="true" />
          </div>

          <section className="home-card qcard">
            <h2>Match preference</h2>
            <p className="muted">A soft nudge for who we pair you with — never a hard wall.</p>
            {/* Toggle chips, not buttons: the previous treatment disabled
                the CHOSEN option, so the global disabled fade made the
                active preference look broken while the inactive one sat
                at full strength — exactly backwards. */}
            <div className="stack-row">
              <button
                type="button"
                className={`tag-chip${matchPref === 'any' ? ' on' : ''}`}
                aria-pressed={matchPref === 'any'}
                disabled={matchPrefSaving || !matchPref}
                onClick={() => void toggleMatchPref('any')}
              >
                Any
              </button>
              <button
                type="button"
                className={`tag-chip${matchPref === 'similar_tags' ? ' on' : ''}`}
                aria-pressed={matchPref === 'similar_tags'}
                disabled={matchPrefSaving || !matchPref}
                onClick={() => void toggleMatchPref('similar_tags')}
              >
                Similar interests
              </button>
            </div>
          </section>

          <section className="home-card qcard graduate">
            <h2>Feeling better?</h2>
            {graduation === 'open' ? (
              <GraduationForm
                communityId={membership.communityId}
                onDone={() => setGraduation('done')}
                onCancel={() => setGraduation('idle')}
              />
            ) : (
              <>
                <p className="muted">
                  Stepping away is also a success on LessAlone — we'll close your matches gently and
                  let your peers know they're getting a fresh pairing. Take a final check-in on the
                  way out so we can learn what helped.
                </p>
                <div className="stack">
                  <button type="button" className="secondary" onClick={() => setGraduation('open')}>
                    Step away well
                  </button>
                </div>
              </>
            )}
          </section>
        </aside>
      </div>

      {/* Quick links — redundant with the desktop rail, but the only path on
          web mobile (no rail there). */}
      {(me.user.isModerator || me.user.isFounder) && (
        <div className="home-oplinks">
          {me.user.isModerator && <Link to="/moderation">Open the moderation tools →</Link>}
          {me.user.isFounder && <Link to="/founder">Open the founder dashboard →</Link>}
        </div>
      )}
    </main>
  );
}
